import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { SERVICES, type Translatable } from '@hrlabs/shared-types';
import { servicePageSaveSchema } from '@hrlabs/validation';
import { getRow, saveRow } from '../../lib/db';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';
import { ArrayField } from '../../components/ArrayField';

type Tr = Translatable;
const empty = (): Tr => ({ en: '', bg: '' });
const tr = (v: unknown): Tr => {
  const o = (v ?? {}) as Partial<Tr>;
  return { en: o.en ?? '', bg: o.bg ?? '' };
};
const list = <T,>(v: unknown, f: (x: Record<string, unknown>) => T): T[] =>
  Array.isArray(v) ? v.map((x) => f((x ?? {}) as Record<string, unknown>)) : [];

interface Form {
  status: 'draft' | 'published';
  h1: Tr;
  metaTitle: Tr;
  metaDescription: Tr;
  heroLead: Tr;
  answer: Tr;
  included: { title: Tr; body: Tr }[];
  who: Tr[];
  notThis: Tr;
  steps: { title: Tr; body: Tr }[];
  hasProof: boolean;
  proof: { quote: Tr; who: Tr };
  faq: { q: Tr; a: Tr }[];
  related: string[];
}

const BLANK: Form = {
  status: 'draft',
  h1: empty(), metaTitle: empty(), metaDescription: empty(), heroLead: empty(), answer: empty(),
  included: [], who: [], notThis: empty(), steps: [],
  hasProof: false, proof: { quote: empty(), who: empty() },
  faq: [], related: [],
};

export function ServiceEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const def = SERVICES.find((s) => s.key === id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState<Form>(BLANK);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!id || !def) return;
    getRow('servicePages', id).then((r) => {
      if (r) {
        const pr = r.proof as Record<string, unknown> | null;
        setF({
          status: r.status === 'published' ? 'published' : 'draft',
          h1: tr(r.h1), metaTitle: tr(r.metaTitle), metaDescription: tr(r.metaDescription),
          heroLead: tr(r.heroLead), answer: tr(r.answer),
          included: list(r.included, (b) => ({ title: tr(b.title), body: tr(b.body) })),
          who: list(r.who, (b) => tr(b)),
          notThis: r.notThis ? tr(r.notThis) : empty(),
          steps: list(r.steps, (b) => ({ title: tr(b.title), body: tr(b.body) })),
          hasProof: !!pr,
          proof: pr ? { quote: tr(pr.quote), who: tr(pr.who) } : BLANK.proof,
          faq: list(r.faq, (b) => ({ q: tr(b.q), a: tr(b.a) })),
          related: Array.isArray(r.related) ? (r.related as unknown[]).map(String) : [],
        });
      }
      setLoading(false);
    });
  }, [id, def]);

  if (!def) return <Navigate to="/services" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      status: f.status,
      h1: f.h1, metaTitle: f.metaTitle, metaDescription: f.metaDescription, heroLead: f.heroLead, answer: f.answer,
      included: f.included, who: f.who,
      notThis: f.notThis.en.trim() || f.notThis.bg.trim() ? f.notThis : null,
      steps: f.steps,
      proof: f.hasProof ? f.proof : null,
      faq: f.faq, related: f.related,
    };
    const parsed = servicePageSaveSchema.safeParse(payload);
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => `${i.path.join('.') || 'form'}: ${i.message}`));
      notify('danger', 'Please fix the highlighted problems.');
      return;
    }
    setErrors([]);
    setSaving(true);
    try {
      await saveRow('servicePages', def.key, parsed.data);
      notify('success', f.status === 'published' ? 'Saved and published.' : 'Saved as draft.');
      navigate('/services');
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;
  const set = <K extends keyof Form>(k: K) => (v: Form[K]) => setF((p) => ({ ...p, [k]: v }));

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 900 }}>
      <p className="mb-1"><Link to="/services">← Service pages</Link></p>
      <h3 className="fw-bold mb-1">{def.name.en}</h3>
      <p className="text-muted small">
        EN <code>/services/{def.slug.en}/</code> · BG <code>/uslugi/{def.slug.bg}/</code>
      </p>

      {errors.length > 0 && (
        <div className="alert alert-warning">
          <strong>Cannot save:</strong>
          <ul className="mb-0">{errors.map((x) => (<li key={x}>{x}</li>))}</ul>
        </div>
      )}

      <div className="mb-4" style={{ maxWidth: 260 }}>
        <label className="form-label fw-semibold">Status</label>
        <select className="form-select" value={f.status} onChange={(e) => set('status')(e.target.value as Form['status'])}>
          <option value="draft">draft (hidden)</option>
          <option value="published">published (live)</option>
        </select>
        <div className="form-text">Publishing requires title, meta, lead and answer in both languages.</div>
      </div>

      <BilingualField label="H1 (page title)" value={f.h1} onChange={set('h1')} rows={1} />
      <BilingualField label="Meta title" value={f.metaTitle} onChange={set('metaTitle')} rows={1} />
      <BilingualField label="Meta description" value={f.metaDescription} onChange={set('metaDescription')} rows={2} />
      <BilingualField label="Hero lead (plain-language definition)" value={f.heroLead} onChange={set('heroLead')} rows={3} />
      <BilingualField label="Answer block (40–80 words, answer-first)" value={f.answer} onChange={set('answer')} rows={4} />

      <ArrayField label="What's included" items={f.included} onChange={set('included')} blank={() => ({ title: empty(), body: empty() })}
        render={(b, s) => (<>
          <BilingualField label="Title" value={b.title} onChange={(v) => s({ ...b, title: v })} rows={1} />
          <BilingualField label="Description" value={b.body} onChange={(v) => s({ ...b, body: v })} rows={2} />
        </>)} />

      <ArrayField label="Who it's for" items={f.who} onChange={set('who')} blank={empty}
        render={(w, s) => <BilingualField label="Bullet" value={w} onChange={s} rows={2} />} />

      <BilingualField label="What this is not (leave empty to hide)" value={f.notThis} onChange={set('notThis')} rows={4} />

      <ArrayField label="How it works (steps)" items={f.steps} onChange={set('steps')} blank={() => ({ title: empty(), body: empty() })}
        render={(b, s) => (<>
          <BilingualField label="Step title" value={b.title} onChange={(v) => s({ ...b, title: v })} rows={1} />
          <BilingualField label="Step description" value={b.body} onChange={(v) => s({ ...b, body: v })} rows={2} />
        </>)} />

      <div className="mb-4">
        <div className="form-check mb-2">
          <input className="form-check-input" type="checkbox" id="hasProof" checked={f.hasProof} onChange={(e) => set('hasProof')(e.target.checked)} />
          <label className="form-check-label fw-semibold" htmlFor="hasProof">Show a client quote</label>
        </div>
        {f.hasProof && (
          <div className="border rounded p-3 bg-light">
            <BilingualField label="Quote" value={f.proof.quote} onChange={(v) => setF((p) => ({ ...p, proof: { ...p.proof, quote: v } }))} rows={3} />
            <BilingualField label="Who said it (role, company type)" value={f.proof.who} onChange={(v) => setF((p) => ({ ...p, proof: { ...p.proof, who: v } }))} rows={1} />
          </div>
        )}
      </div>

      <ArrayField label="Questions & answers (specific to this service)" items={f.faq} onChange={set('faq')} blank={() => ({ q: empty(), a: empty() })} addLabel="Add question"
        render={(b, s) => (<>
          <BilingualField label="Question" value={b.q} onChange={(v) => s({ ...b, q: v })} rows={1} />
          <BilingualField label="Answer (40–60 words, answer-first)" value={b.a} onChange={(v) => s({ ...b, a: v })} rows={3} />
        </>)} />

      <div className="mb-4">
        <label className="form-label fw-semibold">Related services (up to 3)</label>
        <div className="d-flex flex-wrap gap-3">
          {SERVICES.filter((s) => s.key !== def.key).map((s) => {
            const on = f.related.includes(s.key);
            return (
              <label key={s.key} className="form-check-label d-flex align-items-center gap-2">
                <input type="checkbox" className="form-check-input m-0" checked={on}
                  disabled={!on && f.related.length >= 3}
                  onChange={() => set('related')(on ? f.related.filter((k) => k !== s.key) : [...f.related, s.key])} />
                {s.name.en}
              </label>
            );
          })}
        </div>
      </div>

      <button className="btn btn-success" disabled={saving} type="submit">{saving ? 'Saving…' : 'Save'}</button>
    </form>
  );
}
